import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { Toaster } from "./components/ui/sonner";
import { CookieBanner } from "./components/CookieBanner";
import { UpdateNotification, UpdatedToast, OfflineIndicator } from "./components/UpdateNotification";
import { LoginPage } from "./pages/LoginPage";
import LandingPage from "./pages/LandingPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import { DashboardPage } from "./pages/DashboardPage";
import { PatientsPage } from "./pages/PatientsPage";
import { MenuConfigPage } from "./pages/MenuConfigPage";
import { OrdersPage } from "./pages/OrdersPage";
import { KitchenPage } from "./pages/KitchenPage";
import { PatientSheetsPage } from "./pages/PatientSheetsPage";
import { AdminPage } from "./pages/AdminPage";
import { StructuresPage } from "./pages/StructuresPage";
import { ChangePasswordPage } from "./pages/ChangePasswordPage";
import { AccessLogsPage } from "./pages/AccessLogsPage";
import { BillingPage } from "./pages/BillingPage";
import { SettingsPage } from "./pages/SettingsPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { OrderHistoryPage } from "./pages/OrderHistoryPage";
import { FirstLoginSetupPage } from "./pages/FirstLoginSetupPage";
import { RecipesPage } from "./pages/RecipesPage";
import { OrganizationsPage } from "./pages/OrganizationsPage";
import { CateringPage } from "./pages/CateringPage";
import { Layout } from "./components/Layout";
import { Loader2 } from "lucide-react";

const ProtectedRoute = ({ children, superAdminOnly = false, adminOnly = false }) => {
  const { user, loading, isSuperAdmin, isAdmin, isViewingStructure, structureId, requiresSetup } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-700" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if supervisore
  const isSupervisore = user?.role === 'supervisore';

  // Redirect to setup page if user requires first login setup
  if (requiresSetup && window.location.pathname !== '/setup') {
    return <Navigate to="/setup" replace />;
  }

  // Super Admin without active structure goes to structures page (except allowed paths)
  const superAdminAllowedPaths = ['/structures', '/organizations', '/change-password', '/access-logs', '/billing', '/settings'];
  if (isSuperAdmin && !isViewingStructure && !user.structure_id && 
      !superAdminAllowedPaths.includes(window.location.pathname)) {
    return <Navigate to="/structures" replace />;
  }

  // Supervisore without active structure goes to structures page
  const supervisoreAllowedPaths = ['/structures', '/change-password'];
  if (isSupervisore && !isViewingStructure && !user.structure_id &&
      !supervisoreAllowedPaths.includes(window.location.pathname)) {
    return <Navigate to="/structures" replace />;
  }

  // superAdminOnly check - also allow supervisore for /structures
  if (superAdminOnly && !isSuperAdmin) {
    // Special case: supervisore can access /structures
    if (isSupervisore && window.location.pathname === '/structures') {
      // Allow access
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  if (adminOnly && !isAdmin && !isViewingStructure) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-700" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Landing page - redirect to dashboard if logged in
const LandingRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-700" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Landing Pages - redirect to dashboard if logged in */}
      <Route path="/" element={
        <LandingRoute>
          <LandingPage />
        </LandingRoute>
      } />
      <Route path="/welcome" element={
        <LandingRoute>
          <LandingPage />
        </LandingRoute>
      } />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/cookies" element={<PrivacyPage />} />
      
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />

      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* First login setup - requires auth but no layout */}
      <Route path="/setup" element={<FirstLoginSetupPage />} />
      
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      
      <Route path="/organizations" element={
        <ProtectedRoute superAdminOnly>
          <OrganizationsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/structures" element={
        <ProtectedRoute superAdminOnly>
          <StructuresPage />
        </ProtectedRoute>
      } />
      
      <Route path="/billing" element={
        <ProtectedRoute superAdminOnly>
          <BillingPage />
        </ProtectedRoute>
      } />
      
      <Route path="/patients" element={
        <ProtectedRoute>
          <PatientsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/menu" element={
        <ProtectedRoute>
          <MenuConfigPage />
        </ProtectedRoute>
      } />
      
      <Route path="/orders" element={
        <ProtectedRoute>
          <OrdersPage />
        </ProtectedRoute>
      } />
      
      <Route path="/kitchen" element={
        <ProtectedRoute>
          <KitchenPage />
        </ProtectedRoute>
      } />
      
      <Route path="/recipes" element={
        <ProtectedRoute>
          <RecipesPage />
        </ProtectedRoute>
      } />
      
      <Route path="/catering" element={
        <ProtectedRoute>
          <CateringPage />
        </ProtectedRoute>
      } />
      
      <Route path="/sheets" element={
        <ProtectedRoute>
          <PatientSheetsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminPage />
        </ProtectedRoute>
      } />

      <Route path="/history" element={
        <ProtectedRoute>
          <OrderHistoryPage />
        </ProtectedRoute>
      } />

      <Route path="/change-password" element={
        <ProtectedRoute>
          <ChangePasswordPage />
        </ProtectedRoute>
      } />

      <Route path="/access-logs" element={
        <ProtectedRoute superAdminOnly>
          <AccessLogsPage />
        </ProtectedRoute>
      } />

      <Route path="/settings" element={
        <ProtectedRoute superAdminOnly>
          <SettingsPage />
        </ProtectedRoute>
      } />
      
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <OfflineIndicator />
          <AppRoutes />
          <CookieBanner />
          <UpdateNotification />
          <UpdatedToast />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
